// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { CarePlan, Patient } from '@medplum/fhirtypes';
import { PATIENT_TRIAGE_STATUS, type PatientTriageStatus, POLARIS_TRIAGE_STATUS_TAG_SYSTEM } from '../utils/patient';
import { withTenantScope } from '../utils/tenant';

export const RPM_CARE_PLAN_CATEGORY_SYSTEM = 'http://polarissleep.com/care-plan-categories';
export const RPM_CARE_PLAN_CATEGORY_CODE = 'rpm';
export const RPM_QUEUE_TRIAGE_STATUS = PATIENT_TRIAGE_STATUS.remoteMonitoring;

export const RPM_AUTO_ENROLL_TRIAGE_STATUSES = [
  PATIENT_TRIAGE_STATUS.followUp30Day,
  PATIENT_TRIAGE_STATUS.followUp60Day,
  PATIENT_TRIAGE_STATUS.followUp90Day,
  PATIENT_TRIAGE_STATUS.followUp180Day,
  PATIENT_TRIAGE_STATUS.miscFollowUp,
  PATIENT_TRIAGE_STATUS.remoteMonitoring,
] as const satisfies readonly PatientTriageStatus[];

export type RpmAutoEnrollTriageStatus = (typeof RPM_AUTO_ENROLL_TRIAGE_STATUSES)[number];

export function isRpmAutoEnrollTriageStatus(code: string | undefined): code is RpmAutoEnrollTriageStatus {
  return RPM_AUTO_ENROLL_TRIAGE_STATUSES.includes(code as RpmAutoEnrollTriageStatus);
}

export function hasRpmQueueTag(patient: Patient | undefined): boolean {
  return (
    patient?.meta?.tag?.some(
      (tag) => tag.system === POLARIS_TRIAGE_STATUS_TAG_SYSTEM && tag.code === RPM_QUEUE_TRIAGE_STATUS
    ) ?? false
  );
}

export function isRpmCarePlan(carePlan: CarePlan | undefined): boolean {
  return (
    carePlan?.category?.some((category) =>
      category.coding?.some(
        (coding) => coding.system === RPM_CARE_PLAN_CATEGORY_SYSTEM && coding.code === RPM_CARE_PLAN_CATEGORY_CODE
      )
    ) ?? false
  );
}

export function findRpmCarePlan(carePlans: CarePlan[]): CarePlan | undefined {
  return carePlans.find(isRpmCarePlan);
}

/**
 * Build or reactivate the patient's RPM CarePlan. `period.start` records when the current RPM episode began
 * (99453 is billed once per episode); it is set whenever an inactive plan is (re)activated.
 */
export function buildRpmCarePlan(
  patientId: string,
  existing?: CarePlan,
  orgId?: string,
  now: Date = new Date()
): CarePlan {
  const episodeContinues = existing?.status === 'active';
  const carePlan: CarePlan = {
    ...(existing ?? { resourceType: 'CarePlan' }),
    status: 'active',
    ...(episodeContinues ? {} : { period: { start: now.toISOString() } }),
    intent: existing?.intent ?? 'plan',
    subject: { reference: `Patient/${patientId}` },
    title: existing?.title ?? 'Remote Patient Monitoring',
    category: [
      {
        coding: [
          {
            system: RPM_CARE_PLAN_CATEGORY_SYSTEM,
            code: RPM_CARE_PLAN_CATEGORY_CODE,
            display: 'Remote Patient Monitoring',
          },
        ],
      },
    ],
  };
  return withTenantScope(carePlan, orgId);
}

export function revokeRpmCarePlan(carePlan: CarePlan): CarePlan {
  return { ...carePlan, status: 'revoked' };
}
